import Phaser from "phaser";
import i18next from "i18next";
import { ItemSlot } from "../item-slot";
import { ItemUI } from "../item-ui";
import { SpriteLoader } from "../../sprite-loader";
import { COLORS } from "../colors";
import { WindowManager } from "../window-manager";
import { ModernButton } from "../modern-button";
import { BaseWindow } from "./base-window";
import { playSound } from "../../utils/sound-utils";
import { trackEvent } from "../../utils/ga-events";
import { Item, ItemType, SlotType } from "../../models/common";
import { getMaterialOutlineColor } from "../colors";

export class LootWindow extends BaseWindow {
  private extraUI: Phaser.GameObjects.GameObject[] = [];
  private selectedSlotIdx: number | null = null;
  private currentLootBoxX: number = 0;
  private currentLootBoxY: number = 0;
  private pickUpButton: Phaser.GameObjects.Container | null = null;
  private actionButton: Phaser.GameObjects.Container | null = null;
  private pickUpButtonInstance: ModernButton | null = null;
  private actionButtonInstance: ModernButton | null = null;
  // Store loot items for local update
  private _items: Item[] = [];
  private windowX: number = 0;
  private windowY: number = 0;
  private windowWidth: number = 0;
  private windowHeight: number = 0;

  constructor(
    scene: Phaser.Scene,
    windowManager: WindowManager,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    super(scene, windowManager);
    this.setUiLayer(uiLayer);
  }

  show(data: { x: number; y: number; items: Item[] }) {
    this.hide();
    this.currentLootBoxX = data.x;
    this.currentLootBoxY = data.y;
    this.selectedSlotIdx = null;
    // Store items for local update after equip/use
    this._items = data.items ? [...data.items] : [];
    // Ensure buttons are fully cleaned up
    this.hidePickUpButton();
    this.hideActionButton();

    // Create base window elements (background, title, close button)
    let windowLabel = i18next.t("ui:lootBox");
    if (
      this.selectedSlotIdx !== null &&
      data.items &&
      data.items[this.selectedSlotIdx]
    ) {
      const selectedItem: Item = data.items[this.selectedSlotIdx];
      if (selectedItem && selectedItem.definition.name) {
        windowLabel += ` - ${selectedItem.definition.name}`;
      }
    }
    const baseElements = this.createBaseWindow({
      title: windowLabel,
      side: "left",
    });

    // Get window bounds for positioning child elements
    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // Store window bounds for use in other methods
    this.windowX = x;
    this.windowY = y;
    this.windowWidth = width;
    this.windowHeight = height;

    // Item info (upper right corner)
    const itemInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 7, "", {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(1, 0);
    itemInfo.setScrollFactor(0);
    const itemTypeInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 27, "", {
        fontSize: "12px",
        color: "#ffffff",
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    itemTypeInfo.setScrollFactor(0);

    // Item slots grid (3x3)
    const slots: ItemSlot[] = [];
    const itemSprites: Phaser.GameObjects.Image[] = [];
    const gridCols = 4;
    const gridRows = 2;
    const contentWidth = width - this.CONTENT_PADDING_X * 2;
    const contentHeight = height - this.CONTENT_PADDING_Y * 2;
    // Use ItemSlot's standard size
    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const slotPaddingX = (contentWidth - slotSize * gridCols) / (gridCols - 1);
    const slotPaddingY = 8;
    const gridStartX = x + this.CONTENT_PADDING_X;
    const gridStartY = y + this.CONTENT_PADDING_Y + 20;
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        const slotX = gridStartX + col * (slotSize + slotPaddingX);
        const slotY = gridStartY + row * (slotSize + slotPaddingY);
        const slotIdx = row * gridCols + col;
        const hasItem = !!(data.items && data.items[slotIdx]);
        const selected = this.selectedSlotIdx === slotIdx;
        const itemSlot = new ItemSlot(slotX, slotY, hasItem);
        itemSlot.setSelected(selected);
        if (hasItem && data.items[slotIdx].definition.type === ItemType.Material) {
          itemSlot.setMaterialOutlineColor(getMaterialOutlineColor(data.items[slotIdx].definition.price));
        }
        if (hasItem && data.items[slotIdx].definition.type === ItemType.Recipe) {
          itemSlot.setBlueprintMode(true);
        }
        // Create rectangle using ItemSlot method
        itemSlot.createRectangle(this.scene);
        if (itemSlot.rect) {
          itemSlot.rect.on("pointerdown", () => {
            // Play menu-click sound
            playSound(this.scene, "menu-click-sound", null, 0.5);
            if (hasItem) {
              this.selectedSlotIdx = slotIdx;
              slots.forEach((s, idx) => {
                s.setSelected(idx === slotIdx);
                s.applyBorderStyle();
              });
              const hasActionButton = this.hasActionButton(data.items[slotIdx]);
              this.showPickUpButton(
                x,
                y,
                width,
                height,
                slotIdx,
                data.items[slotIdx],
                hasActionButton,
              );
              this.showActionButton(
                x,
                y,
                width,
                height,
                slotIdx,
                data.items[slotIdx],
              );
              // Update window label to show item name
              if (data.items[slotIdx] && data.items[slotIdx].definition.name) {
                itemInfo.setText(`${data.items[slotIdx].definition.name}`);
                const def = data.items[slotIdx].definition;
                const typeStr = def.type === ItemType.Equipment && def.equipType ? def.equipType : def.type;
                itemTypeInfo.setText(typeStr);
              }
            } else {
              this.selectedSlotIdx = null;
              slots.forEach((s) => {
                s.setSelected(false);
                s.applyBorderStyle();
              });
              this.hidePickUpButton();
              this.hideActionButton();
              // Reset window label
              itemInfo.setText("");
              itemTypeInfo.setText("");
            }
          });
        }
        if (hasItem) {
          const item = data.items[slotIdx];
          const sprite = SpriteLoader.createSprite(
            this.scene,
            item.definition.spriteSheet,
            item.definition.spriteX,
            item.definition.spriteY,
            slotX + slotSize / 2,
            slotY + slotSize / 2,
            true,
          );
          itemSprites.push(sprite);
          const ammoIndicator = ItemUI.renderAmmoIndicator(
            this.scene,
            item,
            slotX,
            slotY,
            slotSize,
          );
          if (ammoIndicator) itemSprites.push(ammoIndicator);
          const recipeBadge = ItemUI.renderRecipeBadge(this.scene, item.definition, slotX, slotY);
          if (recipeBadge) itemSprites.push(...recipeBadge);
          const countBadge = ItemUI.renderCountBadge(this.scene, item, slotX, slotY, slotSize);
          if (countBadge) itemSprites.push(...countBadge);
        }
        slots.push(itemSlot);
      }
    }

    // Container
    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      itemInfo,
      itemTypeInfo,
      ...slots.map((s) => s.rect).filter((r) => r !== null),
      ...itemSprites,
    ]);
    this.container.setDepth(110000); // Higher than uiLayer (100000) to ensure proper pointer event handling
    this.visible = true;
    if (this.container) {
      this.container.setVisible(true);
    }

    // Add container to UI layer
    this.uiLayer.add(this.container);
  }

  private hasActionButton(item: Item): boolean {
    if (!item) return false;
    if (
      item.definition.type === "Equipment" ||
      item.definition.type === "Consumable"
    )
      return true;

    /*
        if (item.type === 'Ammo') {
            // Only show USE if player has correct weapon equipped
            const weaponName = item.stats && item.stats.weapon;
            if (!weaponName || !window.player || !window.player.data || !window.player.data.equipment) return false;
            // Find equipped weapon
            const equippedWeapon = Object.values(window.player.data.equipment).find((eq: any) => eq && eq.equipType === 'Weapon' && eq.name && eq.name.toLowerCase() === weaponName.toLowerCase());
            return !!equippedWeapon;
        }
        */
    return false;
  }

  private showPickUpButton(
    x: number,
    y: number,
    width: number,
    height: number,
    slotIdx: number,
    item: Item,
    hasActionButton: boolean,
  ) {
    this.hidePickUpButton();
    const btnY: number = y + height - 8;
    const btnSpacing: number = 8;
    // Capture coordinates at button creation time to avoid stale values
    const lootBoxX: number = this.currentLootBoxX;
    const lootBoxY: number = this.currentLootBoxY;

    const button: ModernButton = new ModernButton({
      scene: this.scene,
      x: x + width / 2,
      y: btnY,
      text: i18next.t("ui:buttons.pickUp") || "PICK UP",
      style: "primary",
      onClick: () => {
        // Play pick-item sound
        playSound(this.scene, "pick-item-sound", null, 0.5);
        trackEvent("item-looted", {
          itemId: item.definition.id,
          itemType: item.definition.type,
        });
        if (typeof window !== "undefined" && (window as any).socket) {
          (window as any).socket.emit("pickup-item", {
            lootBoxX: lootBoxX,
            lootBoxY: lootBoxY,
            itemIdx: slotIdx,
          });
        }
      },
    });

    // Store the button instance for later positioning if there's an action button
    this.pickUpButtonInstance = button;

    // If there's an action button, don't position yet - wait for showActionButton
    if (!hasActionButton) {
      // Position normally if no action button
      const btnWidth: number = button.buttonWidth;
      button.setX(x + width / 2);
    }

    this.pickUpButton = this.scene.add.container(0, 0, [button]);
    this.pickUpButton.setDepth(100001);
    this.pickUpButton.setScrollFactor(0);
    if (this.container) {
      this.container.add(this.pickUpButton);
    } else {
      this.uiLayer.add(this.pickUpButton);
    }
  }

  private showActionButton(
    x: number,
    y: number,
    width: number,
    height: number,
    slotIdx: number,
    item: Item,
  ) {
    this.hideActionButton();

    if (
      item.definition.type !== "Equipment" &&
      item.definition.type !== "Consumable"
    ) {
      return;
    }

    const btnY: number = y + height - 8;
    const btnSpacing: number = 8;
    // Capture coordinates and item at button creation time to avoid stale values
    const lootBoxX: number = this.currentLootBoxX;
    const lootBoxY: number = this.currentLootBoxY;
    const itemRef: Item = item;

    const button: ModernButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: item.definition.type === "Equipment" ? (i18next.t("ui:buttons.equip") || "EQUIP") : (i18next.t("ui:buttons.use") || "USE"),
      style: "success",
      onClick: () => {
        if (typeof window !== "undefined" && (window as any).socket) {
          if (item.definition.type === "Consumable") {
            const itemName = item.definition.name?.toLowerCase() || "";
            if (itemName === "bandage") {
              playSound(this.scene, "bandage", null, 0.5);
            } else if (itemName === "health pack" || itemName === "health pill") {
              playSound(this.scene, "heal", null, 0.5);
            }
            (window as any).socket.emit("use-item", {
              itemId: itemRef.id,
              slotType: SlotType.LOOT,
              lootBoxX: lootBoxX,
              lootBoxY: lootBoxY,
            });
          } else if (item.definition.type === "Equipment") {
            // Play pick-item sound
            playSound(this.scene, "pick-item-sound", null, 0.5);
            (window as any).socket.emit("equip-item", {
              itemId: itemRef.id,
              slotType: SlotType.LOOT,
              lootBoxX: lootBoxX,
              lootBoxY: lootBoxY,
            });
          }
        }
        this.hideActionButton();
      },
    });

    // Store the button instance
    this.actionButtonInstance = button;

    // Position both buttons centered based on their combined width
    const pickUpBtnWidth = this.pickUpButtonInstance?.buttonWidth || 0;
    const actionBtnWidth = button.buttonWidth;
    const totalWidth = pickUpBtnWidth + btnSpacing + actionBtnWidth;
    const centerX = x + width / 2;
    const leftBtnX = centerX - totalWidth / 2 + pickUpBtnWidth / 2;
    const rightBtnX = centerX + totalWidth / 2 - actionBtnWidth / 2;

    if (this.pickUpButtonInstance) {
      this.pickUpButtonInstance.setX(leftBtnX);
    }
    button.setX(rightBtnX);

    this.actionButton = this.scene.add.container(0, 0, [button]);
    this.actionButton.setDepth(100001);
    this.actionButton.setScrollFactor(0);
    if (this.container) {
      this.container.add(this.actionButton);
    } else {
      this.uiLayer.add(this.actionButton);
    }
  }

  private hideActionButton() {
    if (this.actionButton) {
      this.actionButton.destroy();
      this.actionButton = null;
    }
    this.actionButtonInstance = null;
  }

  private hidePickUpButton() {
    if (this.pickUpButton) {
      this.pickUpButton.destroy();
      this.pickUpButton = null;
    }
    this.pickUpButtonInstance = null;
  }

  hide() {
    // Hide buttons first before destroying container
    this.hidePickUpButton();
    this.hideActionButton();
    // Reset selection
    this.selectedSlotIdx = null;
    super.hide();
  }

  getCurrentLootBox(): { x: number; y: number } {
    return {
      x: this.currentLootBoxX,
      y: this.currentLootBoxY,
    };
  }
}
